import {map, pairwise, switchMap, takeLast, takeUntil} from 'rxjs/operators';

import {Injectable} from '@angular/core';
import {ApiService} from './api.service';
import {AbstractControl} from '@angular/forms';
import {MatOption, MatSelect} from '@angular/material';
import {BehaviorSubject, forkJoin, fromEvent, Observable, Observer} from 'rxjs';
import {Message} from '../class/message';
import {ToastrService} from 'ngx-toastr';
// @ts-ignore
import {NzUploadFile} from 'ng-zorro-antd/upload';
import {DomSanitizer} from '@angular/platform-browser';
import {HttpClient, HttpParams} from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { StorageService } from './storage.service';

const routes = {
  allDistricts: '/common/districts',
  allWards: '/common/wards',
  allCities: '/common/cities',
  allDistrictsRedis: '/common/districts-redis',
  allWardsRedis: '/common/wards-redis',
  allCitiesRedis: '/common/cities-redis',
  allEnterpriseType: '/common/item-type/enterprise-type',
  allBankName: '/common/get-banks',
  allEnterpriseServiceType: '/common/enterprise/service-type/',
  certificate: '/common/item-type/enterprise-certificate-type',
  representativeCertificate: '/common/item-type/representative-certificate-type',
  typeOfDocument: (id: string) => `/common/service-type/${id}`,
  allEnterpriseFilterMineServiceType: '/common/enterprise/service-type/filter-mine/',
  autoCompleteBranches: '/common/auto-complete/child-branch',
  autoCompleteCreatedUser: '/common/auto-complete/created-user',
  autoCompleteSignCustomer: '/common/auto-complete/sign-customer',
  autoCompleteSignCustomerByUser: '/common/auto-complete/sign-customer-by-user',
  getAllCloudCaProvider: '/common/get-cloud-ca-providers',
  viewPdfForDocumentCertificate: (id: string) => `/digital-certificate/sign-admin/${id}`,
  changeNewAddress: '/common/mapping-area',
  generateZalo: '/common/zalo-init'
};

@Injectable({
  providedIn: 'root'
})
export class CommonService {
  public sidebarSubscription: BehaviorSubject<any>;

  // check canvas change
  private isCanvasChangeSource = new BehaviorSubject(false);
  public isCanvasChange = this.isCanvasChangeSource.asObservable();
  public currentLanguage: BehaviorSubject<string>;
  entTypeChange: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

// ====
  constructor(
    private apiService: ApiService,
    public message: Message,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private storageService: StorageService,
    private http: HttpClient
  ) {
    const language = localStorage.getItem('__language') || 'vi';
    this.currentLanguage = new BehaviorSubject<string>(language);
  }

  getCurrentLanguage(): Observable<string> {
    return this.currentLanguage.asObservable();
  }

  setCurrentLanguage(lang: string) {
    this.currentLanguage.next(lang);
  }

  resetDatePicker(pickerInput: string, abstractControl: AbstractControl) {
    if (pickerInput == '') {
      abstractControl.reset();
    }
  }

  sanitize(url: string) {
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  moveEvent(index, tab, id) {
    tab = tab + index;
    document.getElementById(tab).click();
    document.getElementById(id).focus();
  }

  downArrow(index, tab, list) {
    if (index + 1 >= list.length) {
      tab = tab + 0;
      document.getElementById(tab).focus();
    } else {
      index = index + 1;
      tab = tab + index;
      document.getElementById(tab).focus();
      document.getElementById(tab).click();
    }
  }

  upArrow(index, tab, list) {
    if (index - 1 < 0) {
      tab = tab + list.length;
      document.getElementById(tab).focus();
      document.getElementById(tab).click();
    } else {
      index = index - 1;
      tab = tab + index;
      document.getElementById(tab).focus();
      document.getElementById(tab).click();
    }
  }

  getAllCity() {
    return this.apiService.get(routes.allCities)
      .pipe(map(res => {
        return res;
      }));
  }

  getAllDistricts() {
    return this.apiService.get(routes.allDistricts)
      .pipe(map(res => {
        return res;
      }));
  }

  getAllWard() {
    return this.apiService.get(routes.allWards)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * Cache-first cho cac API master-data it thay doi (loai KH, ngan hang, giay to, tinh/huyen/xa):
   * hit localStorage (key theo tenant) -> tra ngay; miss -> goi silent roi luu cache.
   * Cac man form (add-customer, update-enterprise, kyc...) khong phai cho blockUI lan thu 2 tro di.
   */
  // cache in-memory cho payload vuot quota localStorage (vi du: wards-redis ~3.8MB)
  private commonDataMemoryCache = new Map<string, any>();

  private getCachedCommonData(cacheKey: string, path: string): Observable<any> {
    const tenantId = localStorage.getItem('tenantId') == null ? '' : localStorage.getItem('tenantId');
    const storageKey = `${tenantId}_${cacheKey}`;

    if (this.commonDataMemoryCache.has(storageKey)) {
      return Observable.create(observer => {
        observer.next(this.commonDataMemoryCache.get(storageKey));
        observer.complete();
      });
    }

    try {
      const cached = localStorage.getItem(storageKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        this.commonDataMemoryCache.set(storageKey, parsed);
        return Observable.create(observer => {
          observer.next(parsed);
          observer.complete();
        });
      }
    } catch (e) { /* cache loi -> fallback goi api */ }

    return this.apiService.getSilently(path).pipe(
      map(res => {
        this.commonDataMemoryCache.set(storageKey, res);
        try {
          localStorage.setItem(storageKey, JSON.stringify(res));
        } catch (e) { /* payload vuot quota -> chi giu cache in-memory trong session */ }
        return res;
      })
    );
  }

  getAllCityRedis(queryParam: string = '') {
    let param = ''
    if(queryParam){
      param = queryParam
    }
    return this.getCachedCommonData('cities-redis' + param, routes.allCitiesRedis + param);
  }

  getAllDistrictsRedis() {
    return this.getCachedCommonData('districts-redis', routes.allDistrictsRedis);
  }

  getAllWardRedis(queryParam: string = '') {
    let param = ''
    if(queryParam){
      param = queryParam
    }
    return this.getCachedCommonData('wards-redis' + param, routes.allWardsRedis + param);
  }

  getEnterpriseType() {
    return this.getCachedCommonData('enterprise-type', routes.allEnterpriseType);
  }
  getBankName() {
    return this.getCachedCommonData('bank-name', routes.allBankName);
  }

  getEnterpriseServiceType(enterpriseId: string) {
    // goi silent (khong blockUI): cac man search/report chi can icon loading trong dropdown
    return this.apiService.getSilently(routes.allEnterpriseServiceType + enterpriseId);
  }

  getServiceTypeAdmin() {
    return this.apiService.get('/users/service-type-admin')
      .pipe(map(res => {
        return res;
      }));
  }

  getEnterpriseFilterMineServiceType(enterpriseId: string) {
    return this.apiService.get(routes.allEnterpriseFilterMineServiceType + enterpriseId)
      .pipe(map(res => {
        return res;
      }));
  }

  getCertificateType() {
    return this.getCachedCommonData('enterprise-certificate-type', routes.certificate);
  }

  getRepresentativeCertificateType() {
    return this.getCachedCommonData('representative-certificate-type', routes.representativeCertificate);
  }

  /**
   * Create an Array<number>[i], value of array element is being counted from [from]
   * @param i: number
   * @param from: number
   */
  counter(index: number, from: number): any {
    if (index === 0 || isNaN(index)) {
      return [];
    }
    return new Array(index).fill(0).map((x, i) => i + from);
  }

  /**
   * Binding city, district, ward from wardId
   */
  bindingAddress(cities, districts, wards, wardId) {
    const selectedWard = wards.find(x => {
      return x.id = wardId;
    });
    const selectedDistrict = districts.find(x => {
      return x.id = selectedWard.districtId;
    });
    const selectedCity = cities.find(x => {
      return x.id = selectedDistrict.cityId;
    });
    const selectedWards = wards.filter(x => {
      return x.districtId == selectedDistrict.id;
    });
    const selectedDistricts = districts.filter(x => {
      return x.cityId == selectedCity.id;
    });
    return {selectedCity, selectedWards, selectedDistricts};
  }

  /**
   * Get city name from cities array
   */
  getCityName(cities, cityId) {
    const selectedCity = cities.find(x => {
      return x.id == cityId;
    });
    if (selectedCity == undefined || selectedCity == null) {
      return {id: null, name: '', description: ''};
    }
    return selectedCity;
  }

  /**
   * Get district name from districts array
   */
  getDistrictName(districts, districtId) {
    const selectedDistrict = districts.find(x => {
      return x.id == districtId;
    });
    if (selectedDistrict == undefined || selectedDistrict == null) {
      return {id: null, cityId: null, name: '', description: ''};
    }
    return selectedDistrict;
  }

  /**
   * Get ward name from wards array
   */
  getWardName(wards, wardId) {
    const selectedWard = wards.find(x => {
      return x.id == wardId;
    });
    if (selectedWard == undefined || selectedWard == null) {
      return {id: null, districtId: null, name: '', description: ''};
    }
    return selectedWard;
  }

  // Get document by id
  getTypeOfDocument(id: string) {
    return this.apiService.get(routes.typeOfDocument(id)).pipe(
      map(rs => {
        return rs;
      })
    );
  }

  /**
   * upload multi file with formdata
   */
  uploadMultiFile(listFile: any) {
    const formdata = new FormData();
    for (let index = 0; index < listFile.length; index++) {
      formdata.append('file', listFile[index]);
    }

    return this.apiService.postUpload('/file/upload/enterprises', formdata)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * upload multi file customer with formdata
   */
  uploadMultiFileCustomer(listFile: any) {
    const formdata = new FormData();
    for (let index = 0; index < listFile.length; index++) {
      formdata.append('file', listFile[index]);
    }

    return this.apiService.postUpload('/file/upload/customer/', formdata)
      .pipe(map(res => {
        return res;
      }));
  }

  downFileTemplateExcelAddCustomer(queryParam: string = '') {
    let param = ''
    if(queryParam){
      param = queryParam
    }
    return this.apiService.getFile('/file/excel/template-add-customer'+param)
      .pipe(map(res => {
        return res;
      }));
  }

  downResultImportExcelAddCustomer(resultImportExcelName: any) {
    return this.apiService.getFileWithName('/file/excel/result-import-excel', resultImportExcelName)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * upload file excel to add customer
   */
  uploadAddCustomerFromExcel(listFile: any, resultImportExcelName: any) {
    const formdata = new FormData();
    formdata.append('file', listFile[0]);
    formdata.append('userId', localStorage.getItem('userId'));
    formdata.append('tenantId', localStorage.getItem('tenantId'));
    formdata.append('enterpriseId', localStorage.getItem('enterpriseId'));
    formdata.append('resultImportExcelName', resultImportExcelName);
    return this.apiService.postUpload2('/file/upload/excel/add-customer', formdata)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * upload file for document with formdata
   */
  uploadFileDocument(file: File) {
    const formdata = new FormData();

    formdata.append('file', file);

    return this.apiService.postUpload('/contract/upload/file/', formdata)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * upload file for document with formdata
   */
  uploadFilesDocument(files: File[]) {
    const formdata = new FormData();

    files.forEach(file => {
      formdata.append('file', file);
    });

    return this.apiService.postUpload('/contract/upload/files/', formdata)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * Convert Vietnamese to non-Vietnamese string
   */
  vietNameseConverter(str: string) {
    if (str.trim().length > 0) {
      str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
      str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
      str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
      str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
      str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
      str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
      str = str.replace(/đ/g, 'd');
      str = str.replace(/À|Á|Ạ|Ả|Ã|Â|Ầ|Ấ|Ậ|Ẩ|Ẫ|Ă|Ằ|Ắ|Ặ|Ẳ|Ẵ/g, 'A');
      str = str.replace(/È|É|Ẹ|Ẻ|Ẽ|Ê|Ề|Ế|Ệ|Ể|Ễ/g, 'E');
      str = str.replace(/Ì|Í|Ị|Ỉ|Ĩ/g, 'I');
      str = str.replace(/Ò|Ó|Ọ|Ỏ|Õ|Ô|Ồ|Ố|Ộ|Ổ|Ỗ|Ơ|Ờ|Ớ|Ợ|Ở|Ỡ/g, 'O');
      str = str.replace(/Ù|Ú|Ụ|Ủ|Ũ|Ư|Ừ|Ứ|Ự|Ử|Ữ/g, 'U');
      str = str.replace(/Ỳ|Ý|Ỵ|Ỷ|Ỹ/g, 'Y');
      str = str.replace(/Đ/g, 'D');
      return str;
    }
    return '';
  }

  /**
   * Filter whether word(s) in another string
   */
  checkFilterWords(str: string, filterWords: string) {
    const filterWordsNonVietnamese = this.vietNameseConverter(filterWords);
    // tslint:disable-next-line:max-line-length
    return str != null && (str.indexOf(filterWords) !== -1 || str.indexOf(filterWordsNonVietnamese) !== -1 || this.vietNameseConverter(str).indexOf(filterWords) !== -1);
  }

  getFileNameFromResponseContentDisposition(rs: Response) {
    const contentDisposition = rs.headers.get('content-disposition') || '';
    const matches = /filename=([^;]+)/ig.exec(contentDisposition);
    const fileName = (matches[1] || 'untitled').trim();
    return fileName;
  }

  /**
   * Download file for enterprise
   */
  downloadFileEnterprises(id: string) {
    return this.apiService.getFile(`/file/download/enterprises/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * Download file for customer
   */
  downloadFileCustomer(id: string) {
    return this.apiService.getFile(`/file/download/customer/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  downloadDocumentFile(id: string, type: string = '') {
    return this.apiService.getFile(`/contract/download-file-upload/${id}` + type)
      .pipe(map(res => {
        return res;
      }));
  }

  // Read file to view pdf
  // tslint:disable-next-line:ban-types
  readFile(file: File, callBack: Function) {
    if (typeof (FileReader) !== 'undefined') {
      const reader = new FileReader();

      reader.onload = (e: any) => {
        const pdfSrc = e.target.result;
        callBack(pdfSrc);
      };

      reader.readAsArrayBuffer(file);
    }
  }

  readFileAsync(file: File) {
    return new Promise((resolve, reject) => {
        if (typeof (FileReader) !== 'undefined') {
          const reader = new FileReader();
          reader.onload = (e: any) => {
            const pdfSrc = e.target.result;
            resolve(pdfSrc)
          };
          reader.readAsArrayBuffer(file);
        } else {
          reject(null);
        }
      }
    )
  }


  // View pdf
  getViewPdf(id: string) {
    return this.apiService.getFile(`/file/viewDocTypePdf/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  // Get list customer for autocomplete
  getCustomers(key: string) {
    return this.getBase('/common/auto-complete/customer?filter=', key);
  }

  // Get list document for autocomplete
  getDocuments(id: string) {
    // this.getListAutoComplete('/common/document', id);
  }

  // Get list creator for autocomplete
  getCreators(id: string) {
    // this.getListAutoComplete('/common/creator', id);
  }

  // Common api get
  private getBase(path: string, id: string) {
    return this.apiService.get(path + id)
      .pipe(map(res => {
        return res;
      }));
  }

  // Get list document type for autocomplete
  getDocumentType(key: string) {
    return this.getBase('/common/auto-complete/document-type?filter=', key);
  }

  // Get list document parent for autocomplete
  getDocumentParent(docsTypeId: string) {
    return this.apiService.get(`/contract/parent-by-name/${docsTypeId}`)
      .pipe(map(res => {
        return res;
      }));
  }

  // Get list document parent for autocomplete
  getDocumentParentFilter(filter: string) {
    return this.apiService.get(`/contract/parent-by-name?filter=${filter}`)
      .pipe(map(res => {
        return res;
      }));
  }

  // View pdf
  getViewPdfForDocument(id: string) {
    return this.apiService.getFile(`/file/view/contract/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  // View pdf silent: man add-document tu quan ly preview, khong block UI toan trang
  getViewPdfForDocumentSilently(id: string) {
    return this.apiService.getFileSilently(`/file/view/contract/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  // View pdf
  getViewPdfsForDocument(id: string) {
    return this.apiService.get(`/file/view/contract-files/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }
  // View pdf
  getViewPdfsForDocumentCertificate(id: any) {
    return this.apiService.getFile(routes.viewPdfForDocumentCertificate(id))
      .pipe(map(res => {
        return res;
      }));
  }

  getViewPdfForDocumentByFilePath(data: ViewDocumentReferenceDto) {
    return this.apiService.getFilePostMethod(`/file/view/contract-by-path`, data)
      .pipe(map(res => {
        return res;
      }));
  }

  // silent: man view-document tu quan ly loading rieng, khong block UI toan trang
  getViewPdfForDocumentByFilePathSilently(data: ViewDocumentReferenceDto) {
    return this.apiService.getFilePostSilently(`/file/view/contract-by-path`, data)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * Check validate for file pdf
   * @param id id contract
   */
  checkFilePdf(id: string) {
    return this.apiService.get(`/file/view/contract/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * Format Date to dd/mm/yyyy
   */
  public formatDate(date: Date) {
    if (!date || !(date instanceof Date && !isNaN(date.getTime()))) {
      return '';
    }
    const dd = date.getDate();
    let newDD = dd.toString();
    const mm = date.getMonth() + 1;
    let newMM = mm.toString();
    const yyyy = date.getFullYear();

    if (dd < 10) {
      newDD = '0' + dd;
    }
    if (mm < 10) {
      newMM = '0' + mm;
    }
    return newDD + '/' + newMM + '/' + yyyy;
  }

  /**
   * Format Date to HH:mm:ss
   */
  public formatTime(date: Date) {
    if (!date || !(date instanceof Date && !isNaN(date.getTime()))) {
      return '';
    }
    const hh = date.getHours();
    const mm = date.getMinutes();
    const ss = date.getSeconds();

    return hh + ':' + mm + ':' + ss;
  }

  formatString(str: string, ...val: string[]) {
    for (let index = 0; index < val.length; index++) {
      str = str.replace(`{${index}}`, val[index]);
    }
    return str;
  }

  /**
   * Capitalize first letter of each word in a string
   */
  public capitalizeFirstLetter(str: string) {
    if (!str) {
      return null;
    }
    const splitStr = str.toLowerCase().split(' ');
    for (let i = 0; i < splitStr.length; i++) {
      splitStr[i] = splitStr[i].charAt(0).toUpperCase() + splitStr[i].substring(1);
    }
    // Directly return the joined string
    return splitStr.join(' ');
  }

  /**
   * Get serviceType by id
   * @param id serviceType ID
   */
  getServiceType(id: string) {
    return this.apiService.get(`/common/service-type/detail/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * Get serviceType by id (silent): khong block UI toan trang
   */
  getServiceTypeSilently(id: string) {
    return this.apiService.getSilently(`/common/service-type/detail/${id}`)
      .pipe(map(res => {
        return res;
      }));
  }

  format(s: string, ...args: Array<any>): string {
    return s.replace(/{(\d+)}/g, (match, argIndex) => {
      if (typeof args[argIndex] != 'undefined') {
        if ('string' === typeof args[argIndex]) {
          return this.escapeHtml(args[argIndex]);
        } else {
          return args[argIndex];
        }
      } else {
        return match;
      }
    });
  }

  formatWithoutEscape(s: string, ...args: Array<any>): string {
    return s.replace(/{(\d+)}/g, (match, argIndex) => {
      if (typeof args[argIndex] != 'undefined') {
        return args[argIndex];
      } else {
        return match;
      }
    });
  }

  /**
   * escapse html string, except Formatting Elements
   * @param unsafe string need to escapsed html
   */
  escapeHtml(unsafe: string) {
    const formattingElems = ['strong', 'b', 'i', 'em', 'mark', 'small', 'del', 'ins', 'sub', 'sup'];
    const startElemKeys = [];
    const endElemKeys = [];
    const rnum = new Date().getTime();
    for (const elem of formattingElems) {
      startElemKeys.push(`${elem}-start-element-${rnum}`);
      endElemKeys.push(`${elem}-end-element-${rnum}`);
    }

    let r = unsafe;
    for (let i = 0; i < formattingElems.length; i++) {
      const rgStart = new RegExp(`<\s*${formattingElems[i]}\s*>`, 'gi');
      const rgEnd = new RegExp(`<\/\s*${formattingElems[i]}\s*>`, 'gi');
      r = r.replace(rgStart, startElemKeys[i]);
      r = r.replace(rgEnd, endElemKeys[i]);
    }

    r = r.replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/'/g, '&quot;')
      .replace(/\//g, '&#x2F;')
      .replace(/'/g, '&#039;');

    for (let i = 0; i < formattingElems.length; i++) {
      r = r.replace(startElemKeys[i], `<${formattingElems[i]}>`);
      r = r.replace(endElemKeys[i], `</${formattingElems[i]}>`);
    }

    return r;
  }

  filterCertificateType(listData, FilterCtrl, filtered) {
    if (!listData) {
      return;
    }
    // get the search keyword
    let search = FilterCtrl.value;
    if (!search) {
      filtered.next(listData.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    search = this.extractUnicode(search);

    // filter the certificateType
    filtered.next(
      listData.filter(item => this.extractUnicode(item.name || item.key).indexOf(search) > -1)
    );
  }

  filterProcedure(listData, FilterCtrl, filtered) {
    if (!listData) {
      return;
    }
    // get the search keyword
    let search = FilterCtrl.value;
    if (!search) {
      filtered.next(listData.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    search = this.extractUnicode(search);

    // filter the certificateType
    filtered.next(
      listData.filter(item => this.extractUnicode(item.name).indexOf(search) > -1)
    );
  }

  /**
   * Remove unicode characters in string
   * @param str the string param
   */
  extractUnicode(str: string) {
    if (!str) {
      return str;
    }
    let result = str.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
    // result = result.replace(/\đ/gm, 'd');
    result = result.replace(/\đ|\Đ/gm, 'd');
    // result = result.replaceAll(/\Đ/gm, 'd');
    return result;
  }

  showListComboBox(element, checkFocus) {
    if (checkFocus) {
      document.getElementById(element).click();
    }
  }

  openComboBox(checkFocus, matSelect: MatSelect) {
    if (checkFocus) {
      setTimeout(() => {
        if (!matSelect.panelOpen) {
          matSelect.open();
        }
      }, 200);
    }
  }

  closeDropdownList(matSelect: MatSelect) {
    setTimeout(() => {
      matSelect.close();
    }, 200);
  }

   b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
    return new Blob(byteArrays, {type: contentType});
  }

  byteToBlob = (bytes, contentType = '') => {
    return new Blob([new Uint8Array(bytes)], {type: contentType})
  }

  getBase64 = (file: File): Promise<string | ArrayBuffer | null> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  /**
   * Doc File thanh chuoi base64 thuan (da bo prefix "data:...;base64,") de day len BE dang JSON.
   */
  getFileBase64 = async (file: File): Promise<string> => {
    const dataUrl = await this.getBase64(file);
    return typeof dataUrl === 'string' ? dataUrl.split(',')[1] : '';
  }

  fileToBlob = async (file: File) => {
    try {
      const b64String = await this.getBase64(file);
      return this.b64toBlob(b64String);
    } catch (e) {
      console.log(e);
      throw e;
    }
  }

  getBase64Avatar = (img: File, callback: (img: string) => void): void => {
    const reader = new FileReader();
    // tslint:disable-next-line:no-non-null-assertion
    reader.addEventListener('load', () => callback(reader.result!.toString()));
    reader.readAsDataURL(img);
  }

  beforeUploadImage = (file: NzUploadFile, fileList: NzUploadFile[]) => {
    return new Observable((observer: Observer<boolean>) => {
      const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/jpg' || file.type === 'image/png';
      if (!isJpgOrPng) {
        this.toastr.error(this.message.getMessage('messages.only_accept_png_jpg'));
        observer.complete();
        return;
      }
      // tslint:disable-next-line:no-non-null-assertion
      const isLt2M = file.size! / 1024 / 1024 < 2;
      if (!isLt2M) {
        this.toastr.error(this.message.getMessage('messages.image_must_than_2m'));
        observer.complete();
        return;
      }
      observer.next(isJpgOrPng && isLt2M);
      observer.complete();
    });
  }

  imageUrlToFile = async (imageUrl) => {
    return fetch(imageUrl)
      .then(r => r.blob())
      .then(blobFile => new File([blobFile], 'fileNameGoesHere', {type: 'image/png'}));
  }

  configCanvas = (canvasEl: HTMLCanvasElement, cx: CanvasRenderingContext2D, width: number, height: number) => {
    // const canvasEl: HTMLCanvasElement = this.canvas.nativeElement;
    canvasEl.width = width;
    canvasEl.height = height;
    cx.lineWidth = 5;
    cx.lineCap = 'round';
    cx.strokeStyle = '#000';
    this.captureEvents(canvasEl, cx);
  }

  captureEvents = (canvasEl: HTMLCanvasElement, cx: CanvasRenderingContext2D) => {
    // this will capture all mousedown events from the canvas element
    fromEvent(canvasEl, 'mousedown')
      .pipe(
        switchMap((e) => {
          // after a mouse down, we'll record all mouse moves
          return fromEvent(canvasEl, 'mousemove')
            .pipe(
              // we'll stop (and unsubscribe) once the user releases the mouse
              // this will trigger a 'mouseup' event
              takeUntil(fromEvent(canvasEl, 'mouseup')),
              // we'll also stop (and unsubscribe) once the mouse leaves the canvas (mouseleave event)
              takeUntil(fromEvent(canvasEl, 'mouseleave')),
              // pairwise lets us get the previous value to draw a line from
              // the previous point to the current point
              pairwise()
            );
        })
      )
      .subscribe((res: [MouseEvent, MouseEvent]) => {
        const rect = canvasEl.getBoundingClientRect();

        // previous and current position with the offset
        const prevPos = {
          x: res[0].clientX - rect.left,
          y: res[0].clientY - rect.top
        };

        const currentPos = {
          x: res[1].clientX - rect.left,
          y: res[1].clientY - rect.top
        };

        // this method we'll implement soon to do the actual drawing
        this.drawOnCanvas(prevPos, currentPos, cx);
      });
  }

  // tslint:disable-next-line:max-line-length
  drawOnCanvas = (prevPos: { x: number, y: number }, currentPos: { x: number, y: number }, cx: CanvasRenderingContext2D, currentColour: string = '#000', brushSize: number = 5) => {
    if (!cx) {
      return;
    }
    this.setCanvasChange(true);
    cx.beginPath();
    if (prevPos) {
      cx.moveTo(prevPos.x, prevPos.y); // from
      cx.lineTo(currentPos.x, currentPos.y);
      cx.closePath();
      cx.strokeStyle = currentColour;
      cx.lineWidth = brushSize;
      cx.stroke();
    }
  }

  setCanvasChange = (state: boolean) => {
    this.isCanvasChangeSource.next(state);
  }

  isNullOrEmpty(str: string) {
    return str === undefined || str === null || str.trim() === '';
  }

  getAutoCompleteBranches(filter: string) {
    let params = new HttpParams();
    params = params.append('filter', filter);
    return this.apiService.get(routes.autoCompleteBranches, params)
      .pipe(map(res => {
        return res;
      }));
  }

  getAutoCompleteCreatedUser(filter: string) {
    let params = new HttpParams();
    params = params.append('filter', filter);
    return this.apiService.get(routes.autoCompleteCreatedUser, params)
      .pipe(map(res => {
        return res;
      }));
  }

  getAutoCompleteSignCustomer(filter: string) {
    let params = new HttpParams();
    params = params.append('filter', filter);
    return this.apiService.get(routes.autoCompleteSignCustomer, params)
      .pipe(map(res => {
        return res;
      }));
  }

  getAutoCompleteSignCustomerByUser(filter: string) {
    let params = new HttpParams();
    params = params.append('filter', filter);
    return this.apiService.get(routes.autoCompleteSignCustomerByUser, params)
      .pipe(map(res => {
        return res;
      }));
  }

  getCategory(): Observable<any> {
    const cities = this.getCachedCommonData('cities?type=ALL', routes.allCities + '?type=ALL');
    const districts = this.getCachedCommonData('districts-redis?type=ALL', routes.allDistrictsRedis + '?type=ALL');
    const wards = this.getCachedCommonData('wards-redis?type=ALL', routes.allWardsRedis + '?type=ALL');
    const citiesNew = this.getCachedCommonData('cities-redis?type=NEW', routes.allCitiesRedis + '?type=NEW');
    const wardsNew = this.getCachedCommonData('wards-redis?type=NEW', routes.allWardsRedis + '?type=NEW');
    return forkJoin([cities, districts, wards, citiesNew, wardsNew]);
  }

  downFileTemplateExcelAddUser() {
    let params = new HttpParams();
    params = params.append('enterpriseId', localStorage.getItem('enterpriseId'));
    return this.apiService.getFile('/file-scope/excel/template-add-user', params)
      .pipe(map(res => {
        return res;
      }));
  }
  downFileTemplateExcelAddIp() {
    return this.apiService.getFile('/file/excel/template-add-ip-allowed')
      .pipe(map(res => {
        return res;
    }));
  }

  uploadAddUserFromExcel(listFile: any, resultImportExcelName: any) {
    const formdata = new FormData();
    formdata.append('file', listFile[0]);
    formdata.append('userId', localStorage.getItem('userId'));
    formdata.append('tenantId', localStorage.getItem('tenantId'));
    formdata.append('enterpriseId', localStorage.getItem('enterpriseId'));
    formdata.append('resultImportExcelName', resultImportExcelName);
    return this.apiService.postUpload2('/file-scope/upload/excel/add-user', formdata)
      .pipe(map(res => {
        return res;
      }));
  }

  downResultImportExcelAddUser(resultImportExcelName: any) {
    return this.apiService.getFileWithName('/file-scope/excel/result-import-user-excel', resultImportExcelName)
      .pipe(map(res => {
        return res;
      }));
  }

  generateUUID() { // Public Domain/MIT
    let d = new Date().getTime();//Timestamp
    let d2 = ((typeof performance !== 'undefined') && performance.now && (performance.now() * 1000)) || 0;//Time in microseconds since page-load or 0 if unsupported
    return 'xxxxxxxx_xxxx_4xxx_yxxx_xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      let r = Math.random() * 16;//random number between 0 and 16
      if (d > 0) {//Use timestamp until depleted
        r = (d + r) % 16 | 0;
        d = Math.floor(d / 16);
      } else {//Use microseconds since page-load if supported
        r = (d2 + r) % 16 | 0;
        d2 = Math.floor(d2 / 16);
      }
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
  }

  getAllCloudCaProvider() {
    return this.apiService.get(routes.getAllCloudCaProvider)
      .pipe(map(res => {
        return res;
      }));
  }

  public getIsLockedEnterprise() {
    if (!localStorage.getItem('isLocked')) {
      return false;
    }
    return localStorage.getItem('isLocked') === 'true';
  }
  public getIsLockedEnterprisev1(data: any) {
    const isLocked = data.status;
    if (isLocked == 1 || isLocked == 3 ) {
      return  true;
    } else {
      return false;
    }
  }
  public getIsLockedEnterprisev2(data: any) {
    const isLocked = data.status;
    if (isLocked == 0 || isLocked == 2) {
      return  true;
    } else {
      return false;
    }
  }
  public notifyData: BehaviorSubject<any> = new BehaviorSubject<any>({});

  public convertConfigAutoNumber(dataApi: any){
    const parseJSON = json => {
        try {
            return JSON.parse(json)
        } catch (error) {
            return null
        }
    }
    let dataApiCopy;
    let template = ''
    dataApiCopy = parseJSON(JSON.stringify(dataApi));
    dataApiCopy['template'].sort((a,b) =>  a.order - b.order);
    dataApiCopy['template'].forEach(item => {
      if(item.configKey === 'NUMBER_RANGE') {
        item.value = dataApi.fromNumber +'-' + dataApi.toNumber,
        item.type = 1,
        item.viewValue = this.message.getMessage('search_form.component_number')
      }
      if(item.configKey === 'SERVICE_TYPE_CODE') {
        item.value = item.configValue,
        item.type = 3,
        item.viewValue = this.message.getMessage('search_form.service_type_code')
      }
      if(item.configKey === 'dd') {
        item.value = String(new Date().getDate()).padStart(2,'0'),
        item.type = 2,
        item.viewValue = this.message.getMessage('analysis.analysis_report_consumption.options.day'),
        item.displayValue = "DD"
      }
      if(item.configKey === 'MM') {
        item.value = String(new Date().getMonth()+1).padStart(2,'0'),
        item.type = 2,
        item.viewValue = this.message.getMessage('analysis.analysis_report_consumption.options.month'),
        item.displayValue = "MM"
      }
      if(item.configKey === 'YY') {
        item.value = String(new Date().getFullYear() % 100),
        item.type = 2,
        item.viewValue = this.message.getMessage('service_type.config_contract_code.year'),
        item.displayValue = "YY"
      }
      if(item.configKey === 'YYYY') {
        item.value = String(new Date().getFullYear()),
        item.type = 2,
        item.viewValue = this.message.getMessage('service_type.config_contract_code.year'),
        item.displayValue = "YYYY"
      }
      if(item.configKey === 'CUSTOMER_INPUT') {
        item.value = item.configValue,
        item.type = 4,
        item.viewValue = item.configValue
      }
    })
    for(const i of dataApiCopy.template){
      if(i.value){
        if(i.configKey === "NUMBER_RANGE") {
          template = template + `${i.value.split("-")[0]}`
        } else {
          template =  template + `${i.value}`
        }
      }
    }
    return template;
  }

  checkEnterpriseType(): boolean {
    const entType = localStorage.getItem('entType') || '';
    // entType === 2: Loại không chứng thực
    return entType === '2';
  }

  // Check người dùng không chứng thực đối TH KKDN
  checkEnterpriseAnonymusType(): boolean {
    const entTypeAnonymus = localStorage.getItem('entTypeAnonymus') || '';
    // entType === 2: Loại không chứng thực
    return entTypeAnonymus === '2';
  }

  //Check tài khoản có mua 2 dịch vụ Chứng thực và không chứng thực không
  checkBuyTwoService():boolean {
    const userServices = JSON.parse(localStorage.getItem('userServices') || '[]')
    return userServices.length >= 2
  }

  //Check tài khoản có mua 2 dịch vụ Chứng thực và không chứng thực không và không phải tài khoản vãng lai va la roleadmin
  checkBuyTwoServiceWithoutHauntAndAdmin():boolean {
    const userServices = JSON.parse(localStorage.getItem('userServices') || '[]');
    const permissions = localStorage.getItem('permissions');
    const isAdminDefault = this.checkDefaultAdmin() === true;
    let isHaunt = false;
    userServices.forEach(item => {
      if (item.userType == 3) {
        // vãng lai
        isHaunt = true;
      }
    });

    return userServices.length >= 2 && isHaunt == false && isAdminDefault
  }

  checkBuyTwoServiceWithoutHauntAndAdminAndPermissionCustomer():boolean {
    const userServices = JSON.parse(localStorage.getItem('userServices') || '[]');
    const permissions = localStorage.getItem('permissions') ? JSON.parse(localStorage.getItem('permissions')) : [];
    const isAdminDefault = this.checkDefaultAdmin() === true;
    let isHaunt = false;
    userServices.forEach(item => {
      if (item.userType == 3) {
        // vãng lai
        isHaunt = true;
      }
    });

    return userServices.length >= 2 && isHaunt == false && (isAdminDefault || permissions.includes(401) || permissions.includes(402) || permissions.includes(403))
  }

  // Kiểm tra tài khoản có phải là admin mặc định không
  checkDefaultAdmin():boolean {
    const isAdminDefault = JSON.parse(localStorage.getItem('isAdminDefault') || 'false')
    return isAdminDefault == true
  }

  public async getMessage(key: string) {
    let message = await this.translateService.get(key).pipe(takeLast(1)).toPromise();
    return message;
  }
  public convertEmptyObjectToNull(obj) {
    if(!obj){
      return null;
    }
    if (JSON.stringify(obj) === "{}") {
      return null
    }
    return obj;
  }

  changeNewAddress(id: string){
    return this.apiService.get(routes.changeNewAddress + '?areaCode=' +id)
      .pipe(map(res => {
        return res;
    }));
  }

  generateChallengeCodeAndState() {
    return this.apiService.post(routes.generateZalo, {})
      .pipe(map(res => {
        return res;
      }));
  }

  // generateChallengeCodeAndState() {
  //   return this.http.post('http://10.207.252.82:8688/scontract-web-api/api/common/pkce', {})
  //     .pipe(map(res => {
  //       return res;
  //     }));
  // }

  /**
   * upload file for document with formdata
   */
  uploadFileDocumentMoha(file: File, isNewApi: boolean = true) {
    const formdata = new FormData();

    formdata.append('file', file);

    return this.apiService.postUpload(isNewApi ? '/contract-employment/upload/file/' : '/contract/upload/file/', formdata)
      .pipe(map(res => {
        return res;
      }));
  }

  /**
   * upload file for document with formdata
   */
  uploadFilesDocumentMoha(files: File[]) {
    const formdata = new FormData();

    files.forEach(file => {
      formdata.append('file', file);
    });

    return this.apiService.postUpload('/contract-employment/upload/files/', formdata)
      .pipe(map(res => {
        return res;
      }));
  }
}
